import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { toast } from 'sonner'
import {
  Plus, Trash2, Copy, ChevronDown, ChevronRight,
  Loader2, ExternalLink, FolderOpen, Search, AlertTriangle,
} from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Group {
  id: string
  agent_connection_id: string
  group_name: string
  group_url: string
  group_fb_id: string | null
  tags: string[] | null
  default_media_folder_ids: string[] | null
  enabled: boolean
  post_type: string
  post_text: string | null
  media_folder_id: string | null
  link_url: string | null
  comment_text: string | null
  post_interval_hours: number
  max_posts_to_scan: number | null
  last_post_at: string | null
  connection: {
    id: string
    fb_page_name: string
    fb_page_id: string | null
    agent_profile_id: string
  } | null
}

interface Connection {
  id: string
  fb_page_name: string
  agent_profile_name: string
}

interface MediaFolder {
  id: string
  name: string
  images_count: number
}

interface ErrorLog {
  error_code: string
  error_detail: string
  page_name: string
  fb_page_id: string | null
  context: string
  created_at: number
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}min`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

function parseBulkUrls(text: string): { name: string; url: string }[] {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  const out: { name: string; url: string }[] = []
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const sep = line.match(/^(.+?)\s*[|\t]\s*(https?:\/\/\S+)$/)
    if (sep) { out.push({ name: sep[1].trim(), url: sep[2].trim() }); continue }
    if (/^https?:\/\//.test(line)) {
      const slug = line.match(/\/groups\/([^/?]+)/)?.[1] ?? 'Grupo'
      out.push({ name: decodeURIComponent(slug).replace(/[-_]/g, ' '), url: line })
      continue
    }
    if (i + 1 < lines.length && /^https?:\/\//.test(lines[i + 1])) {
      out.push({ name: line, url: lines[i + 1] }); i++; continue
    }
  }
  return out
}

/* ------------------------------------------------------------------ */
/*  Inline cell components                                             */
/* ------------------------------------------------------------------ */

function ToggleCell({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`h-5 w-9 rounded-full transition-colors ${value ? 'bg-emerald-500' : 'bg-zinc-700'}`}
    >
      <div className={`h-4 w-4 rounded-full bg-white transition-transform ${value ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
    </button>
  )
}

function EditableNameCell({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  if (!editing) {
    return (
      <span
        onClick={() => { setDraft(value); setEditing(true) }}
        className="truncate text-zinc-300 cursor-text hover:text-zinc-100"
        title="Clique para editar"
      >
        {value}
      </span>
    )
  }

  return (
    <input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { if (draft.trim() && draft !== value) onChange(draft.trim()); setEditing(false) }}
      onKeyDown={e => {
        if (e.key === 'Enter') { if (draft.trim() && draft !== value) onChange(draft.trim()); setEditing(false) }
        if (e.key === 'Escape') setEditing(false)
      }}
      className="w-full rounded border border-indigo-500 bg-zinc-900 px-1 py-0 text-xs text-zinc-200 focus:outline-none"
    />
  )
}

function TextPopoverCell({ postText, commentText, onSave }: {
  postText: string | null
  commentText: string | null
  onSave: (post: string | null, comment: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [draftPost, setDraftPost] = useState(postText || '')
  const [draftComment, setDraftComment] = useState(commentText || '')
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const lines = (postText || '').split('\n').filter(l => l.trim()).length
  const hasText = lines > 0
  const hasComment = !!(commentText?.trim())

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setDraftPost(postText || ''); setDraftComment(commentText || ''); setOpen(!open) }}
        className={`rounded px-1.5 py-0.5 text-[10px] ${hasText ? 'bg-indigo-500/10 text-indigo-400' : 'text-zinc-600 hover:text-zinc-400'}`}
        title={hasText ? `${lines} frase(s)` : 'Sem frases'}
      >
        {hasText ? `${lines}✎` : '—'}
        {hasComment && <span className="ml-0.5 text-amber-400">💬</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-72 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl p-3 space-y-2">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block mb-1">Frases do post (1/linha = aleatório)</label>
            <textarea
              autoFocus
              value={draftPost}
              onChange={e => setDraftPost(e.target.value)}
              rows={3}
              placeholder="Frase 1&#10;Frase 2&#10;Frase 3..."
              className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-semibold block mb-1">Comentários (opcional)</label>
            <textarea
              value={draftComment}
              onChange={e => setDraftComment(e.target.value)}
              rows={2}
              placeholder="Comentário 1&#10;Comentário 2..."
              className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="text-[10px] text-zinc-500 hover:text-zinc-300 px-2 py-1">Cancelar</button>
            <button
              onClick={() => {
                onSave(draftPost.trim() || null, draftComment.trim() || null)
                setOpen(false)
              }}
              className="text-[10px] bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-500 font-medium"
            >
              Salvar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function SelectCell({ value, options, onChange }: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full rounded border border-zinc-700 bg-zinc-800/50 px-1.5 py-0.5 text-xs text-zinc-200 focus:border-indigo-500 focus:outline-none"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function FolderDropdown({ value, folders, onChange, className, placeholder }: {
  value: string | null
  folders: MediaFolder[]
  onChange: (v: string | null) => void
  className?: string
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const folder = folders.find(f => f.id === value)
  const filtered = useMemo(() => {
    if (!q) return folders
    const lower = q.toLowerCase()
    return folders.filter(f => f.name.toLowerCase().includes(lower))
  }, [folders, q])

  // Position the popover relative to the button
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    // Open upward if not enough space below
    if (spaceBelow < 220) {
      setPos({ top: rect.top - 4, left: rect.left })
    } else {
      setPos({ top: rect.bottom + 4, left: rect.left })
    }
  }, [open])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t)) return
      if (popRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const openUpward = open && btnRef.current ? (window.innerHeight - btnRef.current.getBoundingClientRect().bottom < 220) : false

  return (
    <div className={className ?? ''}>
      <button
        ref={btnRef}
        onClick={() => { setOpen(!open); setQ('') }}
        className="w-full truncate rounded border border-zinc-700 bg-zinc-800/50 px-1.5 py-0.5 text-left text-xs text-zinc-200 hover:border-zinc-600 focus:border-indigo-500 focus:outline-none"
        title={folder ? `${folder.name} (${folder.images_count} imgs)` : 'Sem pasta'}
      >
        {folder ? folder.name : <span className="text-zinc-500">{placeholder ?? '— sem pasta —'}</span>}
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          className="w-56 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl"
          style={{
            position: 'fixed',
            zIndex: 9999,
            left: pos.left,
            ...(openUpward ? { bottom: window.innerHeight - pos.top } : { top: pos.top }),
          }}
        >
          <div className="p-1.5">
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar pasta..."
              className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              onClick={() => { onChange(null); setOpen(false) }}
              className={`w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-800 ${!value ? 'text-indigo-400' : 'text-zinc-400'}`}
            >
              — sem pasta —
            </button>
            {filtered.map(f => (
              <button
                key={f.id}
                onClick={() => { onChange(f.id); setOpen(false) }}
                className={`w-full px-3 py-1.5 text-left text-xs hover:bg-zinc-800 ${f.id === value ? 'text-indigo-400' : 'text-zinc-300'}`}
              >
                {f.name} <span className="text-zinc-600">({f.images_count})</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[10px] text-zinc-600">Nenhuma pasta encontrada</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

function MultiFolderDropdown({ selected, folders, onChange }: {
  selected: string[]
  folders: MediaFolder[]
  onChange: (ids: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const filtered = useMemo(() => {
    if (!q) return folders
    const lower = q.toLowerCase()
    return folders.filter(f => f.name.toLowerCase().includes(lower))
  }, [folders, q])

  const selectedFolders = useMemo(() => folders.filter(f => selected.includes(f.id)), [folders, selected])

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id])
  }

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    if (spaceBelow < 220) {
      setPos({ top: rect.top - 4, left: rect.left })
    } else {
      setPos({ top: rect.bottom + 4, left: rect.left })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t)) return
      if (popRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const openUpward = open && btnRef.current ? (window.innerHeight - btnRef.current.getBoundingClientRect().bottom < 220) : false

  return (
    <div>
      <button
        ref={btnRef}
        onClick={() => { setOpen(!open); setQ('') }}
        className="w-full truncate rounded border border-zinc-700 bg-zinc-800/50 px-1.5 py-0.5 text-left text-xs text-zinc-200 hover:border-zinc-600 focus:border-indigo-500 focus:outline-none"
        title={selectedFolders.map(f => f.name).join(', ') || 'Sem pasta'}
      >
        {selectedFolders.length === 0
          ? <span className="text-zinc-500">— sem pasta —</span>
          : selectedFolders.length === 1
            ? selectedFolders[0].name
            : <span>{selectedFolders.length} pastas</span>
        }
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          className="w-60 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl"
          style={{
            position: 'fixed',
            zIndex: 9999,
            left: pos.left,
            ...(openUpward ? { bottom: window.innerHeight - pos.top } : { top: pos.top }),
          }}
        >
          <div className="p-1.5">
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar pasta..."
              className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {selected.length > 0 && (
              <button
                onClick={() => onChange([])}
                className="w-full px-3 py-1 text-left text-[10px] text-zinc-500 hover:bg-zinc-800 border-b border-zinc-800"
              >
                Limpar seleção
              </button>
            )}
            {filtered.map(f => (
              <label
                key={f.id}
                className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-zinc-800 cursor-pointer"
              >
                <input type="checkbox" checked={selected.includes(f.id)} onChange={() => toggle(f.id)} className="rounded" />
                <span className={selected.includes(f.id) ? 'text-indigo-400' : 'text-zinc-300'}>{f.name}</span>
                <span className="ml-auto text-zinc-600">({f.images_count})</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[10px] text-zinc-600">Nenhuma pasta encontrada</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Add Group Modal (inline)                                           */
/* ------------------------------------------------------------------ */

function AddGroupInline({ connectionId, allGroups, onDone }: {
  connectionId: string
  allGroups: Group[]
  onDone: () => void
}) {
  const [mode, setMode] = useState<'url' | 'existing'>('existing')
  const [text, setText] = useState('')
  const [filterQ, setFilterQ] = useState('')
  const [tagFilter, setTagFilter] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const qc = useQueryClient()

  // All unique tags
  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const g of allGroups) for (const t of g.tags ?? []) set.add(t)
    return Array.from(set).sort()
  }, [allGroups])

  // Groups from OTHER pages, deduped by URL, excluding already on this page
  const thisPageUrls = useMemo(() => {
    return new Set(allGroups.filter(g => g.agent_connection_id === connectionId).map(g => g.group_url))
  }, [allGroups, connectionId])

  const availableGroups = useMemo(() => {
    const seen = new Map<string, Group>()
    for (const g of allGroups) {
      if (g.agent_connection_id === connectionId) continue
      if (thisPageUrls.has(g.group_url)) continue
      if (!seen.has(g.group_url)) seen.set(g.group_url, g)
    }
    let list = Array.from(seen.values())
    if (tagFilter) {
      list = list.filter(g => (g.tags ?? []).includes(tagFilter))
    }
    if (filterQ) {
      const q = filterQ.toLowerCase()
      list = list.filter(g => g.group_name.toLowerCase().includes(q) || g.group_url.toLowerCase().includes(q))
    }
    return list
  }, [allGroups, connectionId, thisPageUrls, filterQ, tagFilter])

  const toggleSelect = (url: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(url) ? next.delete(url) : next.add(url)
      return next
    })
  }

  const submitUrls = async () => {
    const parsed = parseBulkUrls(text)
    if (parsed.length === 0) { toast.error('Nenhuma URL valida'); return }
    const loadingId = toast.loading(`Adicionando ${parsed.length} grupo(s)...`)
    let ok = 0
    for (const entry of parsed) {
      try {
        await api.post('/automacao-fb/page-groups', {
          connection_id: connectionId, group_name: entry.name, group_url: entry.url,
        })
        ok++
      } catch {}
    }
    toast.dismiss(loadingId)
    toast.success(`${ok} grupo(s) adicionado(s)`)
    qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] })
    setText('')
    onDone()
  }

  const submitExisting = async () => {
    if (selected.size === 0) return
    const toAdd = allGroups.filter(g => selected.has(g.group_url) && g.agent_connection_id !== connectionId)
    const seen = new Set<string>()
    const unique = toAdd.filter(g => { if (seen.has(g.group_url)) return false; seen.add(g.group_url); return true })
    const loadingId = toast.loading(`Adicionando ${unique.length} grupo(s)...`)
    let ok = 0
    for (const g of unique) {
      try {
        await api.post('/automacao-fb/page-groups', {
          connection_id: connectionId, group_name: g.group_name, group_url: g.group_url,
        })
        ok++
      } catch {}
    }
    toast.dismiss(loadingId)
    toast.success(`${ok} grupo(s) adicionado(s)`)
    qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] })
    setSelected(new Set())
    onDone()
  }

  return (
    <div className="border-t border-zinc-700/50 bg-zinc-800/30 px-4 py-3 space-y-2">
      {/* Mode toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => setMode('existing')}
          className={`rounded-lg px-3 py-1 text-[11px] font-medium transition-colors ${mode === 'existing' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
        >
          Grupos existentes
        </button>
        <button
          onClick={() => setMode('url')}
          className={`rounded-lg px-3 py-1 text-[11px] font-medium transition-colors ${mode === 'url' ? 'bg-indigo-600 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
        >
          Colar URLs
        </button>
      </div>

      {mode === 'url' ? (
        <>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Cole URLs dos grupos (uma por linha)&#10;Aceita: URL pura, Nome | URL, Nome + URL alternados"
            rows={3}
            autoFocus
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900/50 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
          />
          <div className="flex gap-2">
            <button onClick={submitUrls} disabled={!text.trim()} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-40">
              Adicionar
            </button>
            <button onClick={onDone} className="rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200">Cancelar</button>
          </div>
        </>
      ) : (
        <>
          <input
            value={filterQ}
            onChange={e => setFilterQ(e.target.value)}
            placeholder="Buscar grupo..."
            autoFocus
            className="w-full rounded-lg border border-zinc-700 bg-zinc-900/50 px-3 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
          />
          {allTags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              <button
                onClick={() => setTagFilter(null)}
                className={`rounded-full px-2 py-0.5 text-[10px] ${!tagFilter ? 'bg-indigo-600/30 text-indigo-300' : 'border border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}
              >
                Todas
              </button>
              {allTags.map(t => (
                <button
                  key={t}
                  onClick={() => { setTagFilter(tagFilter === t ? null : t); setSelected(new Set()) }}
                  className={`rounded-full px-2 py-0.5 text-[10px] ${tagFilter === t ? 'bg-indigo-600/30 text-indigo-300' : 'border border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {availableGroups.length === 0 && (
              <div className="py-3 text-center text-[11px] text-zinc-600">
                {filterQ ? 'Nenhum grupo encontrado' : 'Todos os grupos ja estao nesta pagina'}
              </div>
            )}
            {availableGroups.map(g => {
              const slug = g.group_url.match(/\/groups\/([^/?]+)/)?.[1] ?? ''
              const isNumericName = /^\d+$/.test(g.group_name.trim())
              return (
                <label key={g.group_url} className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 cursor-pointer">
                  <input type="checkbox" checked={selected.has(g.group_url)} onChange={() => toggleSelect(g.group_url)} className="rounded mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{g.group_name}</div>
                    {slug && <div className="truncate text-[10px] text-zinc-500">facebook.com/groups/{slug}</div>}
                    {(g.tags ?? []).length > 0 && (
                      <div className="flex gap-0.5 mt-0.5 flex-wrap">
                        {(g.tags ?? []).map(t => <span key={t} className="rounded-full bg-indigo-600/15 px-1.5 py-0 text-[9px] text-indigo-400">{t}</span>)}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-600 shrink-0">{g.connection?.fb_page_name}</span>
                </label>
              )
            })}
          </div>
          <div className="flex gap-2">
            <button onClick={submitExisting} disabled={selected.size === 0} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-40">
              Adicionar {selected.size > 0 ? `(${selected.size})` : ''}
            </button>
            {availableGroups.length > 0 && (
              <button
                onClick={() => setSelected(new Set(availableGroups.map(g => g.group_url)))}
                className="rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200"
              >
                Todos
              </button>
            )}
            <button onClick={onDone} className="rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200">Cancelar</button>
          </div>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Clone Modal                                                        */
/* ------------------------------------------------------------------ */

function CloneModal({ sourceConnectionId, connections, groups, onClose }: {
  sourceConnectionId: string
  connections: Connection[]
  groups: Group[]
  onClose: () => void
}) {
  const [targets, setTargets] = useState<Set<string>>(new Set())
  const qc = useQueryClient()
  const [cloning, setCloning] = useState(false)

  const sourceGroups = groups.filter(g => g.agent_connection_id === sourceConnectionId)
  const otherConns = connections.filter(c => c.id !== sourceConnectionId)

  const handleClone = async () => {
    if (targets.size === 0) return
    setCloning(true)
    const loadingId = toast.loading(`Duplicando ${sourceGroups.length} grupos para ${targets.size} página(s)...`)

    try {
      const res = await api.post<{ created: number; updated: number }>('/automacao-fb/page-groups/clone', {
        source_connection_id: sourceConnectionId,
        target_connection_ids: Array.from(targets),
      })
      toast.dismiss(loadingId)
      toast.success(`${res.created} criados, ${res.updated} atualizados`)
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] })
      onClose()
    } catch {
      // Fallback: clone manually
      let created = 0, updated = 0
      for (const targetId of targets) {
        const targetGroups = groups.filter(g => g.agent_connection_id === targetId)
        const targetByUrl = new Map(targetGroups.map(g => [g.group_url, g]))

        for (const sg of sourceGroups) {
          const patch = {
            post_type: sg.post_type, post_text: sg.post_text ?? '',
            media_folder_id: sg.media_folder_id ?? '', link_url: sg.link_url ?? '',
            comment_text: sg.comment_text ?? '', post_interval_hours: sg.post_interval_hours,
            max_posts_to_scan: sg.max_posts_to_scan, enabled: sg.enabled,
          }
          const existing = targetByUrl.get(sg.group_url)
          if (existing) {
            await api.put(`/automacao-fb/page-groups/${existing.id}`, patch)
            updated++
          } else {
            const r = await api.post<{ ok: boolean; group: { id: string } }>('/automacao-fb/page-groups', {
              connection_id: targetId, group_name: sg.group_name, group_url: sg.group_url,
            })
            await api.put(`/automacao-fb/page-groups/${r.group.id}`, patch)
            created++
          }
        }
      }
      toast.dismiss(loadingId)
      toast.success(`${created} criados, ${updated} atualizados`)
      qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] })
      onClose()
    } finally {
      setCloning(false)
    }
  }

  const toggle = (id: string) => {
    setTargets(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-zinc-200">Duplicar {sourceGroups.length} grupos para:</h3>
        <div className="mt-3 max-h-64 overflow-y-auto space-y-1">
          {otherConns.map(c => (
            <label key={c.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 cursor-pointer">
              <input type="checkbox" checked={targets.has(c.id)} onChange={() => toggle(c.id)} className="rounded" />
              <span className="truncate">{c.fb_page_name}</span>
              <span className="ml-auto text-[10px] text-zinc-500">{c.agent_profile_name}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-200">Cancelar</button>
          <button
            onClick={handleClone}
            disabled={targets.size === 0 || cloning}
            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
          >
            {cloning ? <Loader2 className="h-3 w-3 animate-spin" /> : `Duplicar para ${targets.size} página(s)`}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Batch Text Popover                                                 */
/* ------------------------------------------------------------------ */

function BatchTextPopover({ connectionId, groups: allGroups, onApply }: {
  connectionId: string
  groups: Group[]
  onApply: (connId: string, post: string | null, comment: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const pageGroups = allGroups.filter(g => g.agent_connection_id === connectionId)
  const firstText = pageGroups.find(g => g.post_text)?.post_text || ''
  const firstComment = pageGroups.find(g => g.comment_text)?.comment_text || ''
  const [draftPost, setDraftPost] = useState(firstText)
  const [draftComment, setDraftComment] = useState(firstComment)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setDraftPost(firstText); setDraftComment(firstComment); setOpen(!open) }}
        className="rounded px-1.5 py-0.5 text-[10px] text-indigo-400 hover:bg-indigo-500/10"
        title="Aplicar frases em lote a todos os grupos dessa página"
      >
        ✎ Texto lote
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-40 w-80 rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl p-3 space-y-2">
          <p className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider">Aplicar a {pageGroups.length} grupos</p>
          <div>
            <label className="text-[10px] text-zinc-500 block mb-1">Frases do post (1/linha = aleatório)</label>
            <textarea
              autoFocus
              value={draftPost}
              onChange={e => setDraftPost(e.target.value)}
              rows={4}
              placeholder="Frase 1&#10;Frase 2&#10;Frase 3..."
              className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 block mb-1">Comentários (opcional)</label>
            <textarea
              value={draftComment}
              onChange={e => setDraftComment(e.target.value)}
              rows={2}
              placeholder="Comentário 1&#10;Comentário 2..."
              className="w-full rounded border border-zinc-700 bg-zinc-800 px-2 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="text-[10px] text-zinc-500 hover:text-zinc-300 px-2 py-1">Cancelar</button>
            <button
              onClick={() => {
                onApply(connectionId, draftPost.trim() || null, draftComment.trim() || null)
                setOpen(false)
              }}
              className="text-[10px] bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-500 font-medium"
            >
              Aplicar a todos
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export function GroupsGridTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [cloningFrom, setCloningFrom] = useState<string | null>(null)
  const [profileIdx, setProfileIdx] = useState(0)

  // ── Queries ──
  const { data: groupsRaw, isLoading } = useQuery<{ groups: Group[] }>({
    queryKey: ['automacao-fb', 'all-groups'],
    queryFn: () => api.get('/automacao-fb/page-groups'),
    refetchInterval: 30000,
  })

  const { data: connsRaw } = useQuery<{ connections: Connection[] }>({
    queryKey: ['automacao-fb', 'connections'],
    queryFn: () => api.get('/automacao-fb/connections'),
  })

  const { data: foldersRaw } = useQuery<any[]>({
    queryKey: ['media-folders'],
    queryFn: () => api.get('/media-folders'),
  })

  const { data: errorsRaw } = useQuery<{ logs: ErrorLog[] }>({
    queryKey: ['automacao-fb', 'error-logs-24h'],
    queryFn: () => api.get('/automacao-fb/error-logs?hours=24'),
    refetchInterval: 60000,
  })

  const groups: Group[] = groupsRaw?.groups ?? []
  const connections: Connection[] = connsRaw?.connections ?? []
  const folders: MediaFolder[] = useMemo(() => {
    const raw = Array.isArray(foldersRaw) ? foldersRaw : []
    return raw.map((f: any) => ({
      id: f.id,
      name: f.name,
      images_count: f.images?.length ?? f.images_count ?? 0,
    }))
  }, [foldersRaw])

  // Errors grouped by page name (last 24h)
  const errorsByPage = useMemo(() => {
    const logs = errorsRaw?.logs ?? []
    const map = new Map<string, { count: number; lastError: string; lastCode: string }>()
    for (const l of logs) {
      const key = l.page_name
      if (!key) continue
      const existing = map.get(key)
      if (!existing) {
        map.set(key, { count: 1, lastError: l.error_detail, lastCode: l.error_code })
      } else {
        existing.count++
      }
    }
    return map
  }, [errorsRaw])

  // ── Mutations ──
  const invalidate = () => qc.invalidateQueries({ queryKey: ['automacao-fb', 'all-groups'] })

  const updateGroup = useMutation({
    mutationFn: ({ id, ...data }: { id: string; [key: string]: unknown }) =>
      api.put(`/automacao-fb/page-groups/${id}`, data),
    onSuccess: () => invalidate(),
    onError: () => toast.error('Erro ao atualizar'),
  })

  const deleteGroup = useMutation({
    mutationFn: (id: string) => api.delete(`/automacao-fb/page-groups/${id}`),
    onSuccess: () => { invalidate(); toast.success('Grupo removido') },
    onError: () => toast.error('Erro ao remover'),
  })

  // ── Group data by profile → page ──
  const tree = useMemo(() => {
    // Build profile → pages → groups tree
    const profileMap = new Map<string, {
      profileName: string
      pages: Map<string, { conn: Connection; groups: Group[] }>
    }>()

    // Seed with all connections
    for (const c of connections) {
      if (!profileMap.has(c.agent_profile_name)) {
        profileMap.set(c.agent_profile_name, { profileName: c.agent_profile_name, pages: new Map() })
      }
      profileMap.get(c.agent_profile_name)!.pages.set(c.id, { conn: c, groups: [] })
    }

    // Add groups to their pages
    for (const g of groups) {
      for (const [, profile] of profileMap) {
        const page = profile.pages.get(g.agent_connection_id)
        if (page) {
          // Apply search filter
          if (search) {
            const q = search.toLowerCase()
            const match = g.group_name.toLowerCase().includes(q) ||
              g.group_url.toLowerCase().includes(q) ||
              page.conn.fb_page_name.toLowerCase().includes(q) ||
              profile.profileName.toLowerCase().includes(q)
            if (!match) continue
          }
          page.groups.push(g)
        }
      }
    }

    return Array.from(profileMap.entries()).map(([name, data]) => ({
      profileName: name,
      pages: Array.from(data.pages.values())
        .filter(p => p.groups.length > 0 || !search) // Show empty pages only when not searching
        .sort((a, b) => b.groups.length - a.groups.length || a.conn.fb_page_name.localeCompare(b.conn.fb_page_name)),
      totalGroups: Array.from(data.pages.values()).reduce((sum, p) => sum + p.groups.length, 0),
      enabledGroups: Array.from(data.pages.values()).reduce((sum, p) => sum + p.groups.filter(g => g.enabled).length, 0),
    })).filter(p => p.pages.length > 0)
  }, [groups, connections, search])

  const toggleCollapse = useCallback((key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const handleUpdate = useCallback((groupId: string, field: string, value: unknown) => {
    updateGroup.mutate({ id: groupId, [field]: value })
  }, [updateGroup])

  // ── Batch operations ──
  const batchToggle = useCallback(async (connectionId: string, enabled: boolean) => {
    const pageGroups = groups.filter(g => g.agent_connection_id === connectionId)
    const loadingId = toast.loading(`${enabled ? 'Ativando' : 'Desativando'} ${pageGroups.length} grupos...`)
    for (const g of pageGroups) {
      await api.put(`/automacao-fb/page-groups/${g.id}`, { enabled }).catch(() => {})
    }
    toast.dismiss(loadingId)
    toast.success(`${pageGroups.length} grupos ${enabled ? 'ativados' : 'desativados'}`)
    invalidate()
  }, [groups])

  const batchSetFolder = useCallback(async (connectionId: string, folderId: string | null) => {
    const pageGroups = groups.filter(g => g.agent_connection_id === connectionId)
    const loadingId = toast.loading(`Aplicando pasta a ${pageGroups.length} grupos...`)
    for (const g of pageGroups) {
      if (folderId) {
        // Add to default_media_folder_ids if not already there
        const current = g.default_media_folder_ids ?? []
        if (!current.includes(folderId)) {
          await api.put(`/automacao-fb/page-groups/${g.id}`, { default_media_folder_ids: [...current, folderId] }).catch(() => {})
        }
      } else {
        await api.put(`/automacao-fb/page-groups/${g.id}`, { default_media_folder_ids: [] }).catch(() => {})
      }
    }
    toast.dismiss(loadingId)
    toast.success(`Pasta aplicada a ${pageGroups.length} grupos`)
    invalidate()
  }, [groups])

  const batchSetText = useCallback(async (connectionId: string, postText: string | null, commentText: string | null) => {
    const pageGroups = groups.filter(g => g.agent_connection_id === connectionId)
    const loadingId = toast.loading(`Aplicando texto a ${pageGroups.length} grupos...`)
    for (const g of pageGroups) {
      await api.put(`/automacao-fb/page-groups/${g.id}`, { post_text: postText, comment_text: commentText }).catch(() => {})
    }
    toast.dismiss(loadingId)
    toast.success(`Texto aplicado a ${pageGroups.length} grupos`)
    invalidate()
  }, [groups])

  // ── Render ──
  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>
  }

  const INTERVAL_OPTIONS = [
    { value: '4', label: '4h' }, { value: '8', label: '8h' },
    { value: '12', label: '12h' }, { value: '24', label: '24h' },
    { value: '48', label: '48h' },
  ]

  const TYPE_OPTIONS = [
    { value: 'image', label: 'Imagem' },
    { value: 'link_preview', label: 'Link' },
  ]

  // Clamp profile index
  const safeIdx = tree.length > 0 ? Math.min(profileIdx, tree.length - 1) : 0
  const profile = tree[safeIdx] ?? null

  return (
    <div className="space-y-1">
      {/* Top bar: profile selector + search */}
      <div className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur px-1 py-2 space-y-2">
        {/* Profile pagination */}
        {tree.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setProfileIdx(i => Math.max(0, i - 1))}
              disabled={safeIdx === 0}
              className="rounded-lg border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ←
            </button>
            <select
              value={safeIdx}
              onChange={e => setProfileIdx(Number(e.target.value))}
              className="flex-1 rounded-lg border border-zinc-700 bg-zinc-800/50 px-3 py-1 text-xs text-zinc-200 focus:border-indigo-500 focus:outline-none"
            >
              {tree.map((p, i) => (
                <option key={p.profileName} value={i}>
                  {p.profileName} — {p.enabledGroups}/{p.totalGroups} ativos · {p.pages.length} páginas
                </option>
              ))}
            </select>
            <button
              onClick={() => setProfileIdx(i => Math.min(tree.length - 1, i + 1))}
              disabled={safeIdx >= tree.length - 1}
              className="rounded-lg border border-zinc-700 px-2.5 py-1 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              →
            </button>
            <span className="text-[10px] text-zinc-500 shrink-0">{safeIdx + 1}/{tree.length}</span>
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar página ou grupo..."
              className="w-full rounded-lg border border-zinc-700 bg-zinc-800/50 py-1.5 pl-8 pr-3 text-xs text-zinc-200 placeholder:text-zinc-500 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <span className="text-[10px] text-zinc-500 shrink-0">
            {profile ? `${profile.totalGroups} grupos` : '0 grupos'}
          </span>
        </div>
      </div>

      {tree.length === 0 && (
        <div className="py-12 text-center text-xs text-zinc-500">
          {search ? 'Nenhum resultado' : 'Nenhum perfil configurado'}
        </div>
      )}

      {/* Current profile pages */}
      {profile && (
        <div className="space-y-1">
          {profile.pages.map(({ conn, groups: pageGroups }) => {
            const pageErrors = errorsByPage.get(conn.fb_page_name)
            return (
            <div key={conn.id} className={`rounded-xl border overflow-hidden ${pageErrors ? 'border-amber-600/50' : 'border-zinc-800'}`}>
              {/* Error alert */}
              {pageErrors && (
                <div className="flex items-center gap-2 bg-amber-950/40 px-3 py-1.5 border-b border-amber-600/30">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                  <span className="text-[11px] text-amber-400 truncate">
                    {pageErrors.count} erro(s) nas ultimas 24h — {pageErrors.lastCode}: {pageErrors.lastError}
                  </span>
                </div>
              )}

              {/* Page header */}
              <div className="flex items-center gap-2 bg-zinc-800/60 px-3 py-1.5">
                <button
                  onClick={() => toggleCollapse(`page:${conn.id}`)}
                  className="flex items-center gap-1.5 min-w-0 flex-1"
                >
                  {collapsed.has(`page:${conn.id}`)
                    ? <ChevronRight className="h-3 w-3 text-zinc-500 shrink-0" />
                    : <ChevronDown className="h-3 w-3 text-zinc-500 shrink-0" />
                  }
                  <FolderOpen className={`h-3 w-3 shrink-0 ${pageErrors ? 'text-amber-400' : 'text-indigo-400'}`} />
                  <span className="text-[11px] font-medium text-zinc-200 truncate">{conn.fb_page_name}</span>
                  <span className="text-[10px] text-zinc-500 shrink-0">
                    {pageGroups.filter(g => g.enabled).length}/{pageGroups.length}
                  </span>
                </button>

                {/* Page actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => batchToggle(conn.id, true)}
                    className="rounded px-1.5 py-0.5 text-[10px] text-emerald-400 hover:bg-emerald-500/10"
                    title="Ativar todos"
                  >
                    ✓ Todos
                  </button>
                  <button
                    onClick={() => batchToggle(conn.id, false)}
                    className="rounded px-1.5 py-0.5 text-[10px] text-zinc-500 hover:bg-zinc-700/50"
                    title="Desativar todos"
                  >
                    ✗ Nenhum
                  </button>

                  {/* Batch folder */}
                  <FolderDropdown
                    value={null}
                    folders={folders}
                    onChange={v => batchSetFolder(conn.id, v)}
                    className="w-24"
                    placeholder="Pasta lote"
                  />

                  {/* Batch text */}
                  <BatchTextPopover
                    connectionId={conn.id}
                    groups={groups}
                    onApply={batchSetText}
                  />

                  <button
                    onClick={() => setCloningFrom(conn.id)}
                    className="rounded p-1 text-zinc-500 hover:bg-zinc-700/50 hover:text-zinc-300"
                    title="Duplicar grupos para outra página"
                  >
                    <Copy className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => setAddingTo(addingTo === conn.id ? null : conn.id)}
                    className="rounded p-1 text-indigo-400 hover:bg-indigo-500/10"
                    title="Adicionar grupos"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Add group inline */}
              {addingTo === conn.id && (
                <AddGroupInline connectionId={conn.id} allGroups={groups} onDone={() => setAddingTo(null)} />
              )}

              {/* Groups table */}
              {!collapsed.has(`page:${conn.id}`) && pageGroups.length > 0 && (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-800/30 text-[10px] uppercase tracking-wider text-zinc-600">
                      <th className="py-1 pl-4 pr-2 text-left font-medium w-[30%]">Grupo</th>
                      <th className="py-1 px-1 text-center font-medium w-[50px]">Ativo</th>
                      <th className="py-1 px-1 text-left font-medium w-[80px]">Tipo</th>
                      <th className="py-1 px-1 text-left font-medium w-[25%]">Pasta</th>
                      <th className="py-1 px-1 text-left font-medium w-[60px]">Interv.</th>
                      <th className="py-1 px-1 text-center font-medium w-[50px]">Texto</th>
                      <th className="py-1 px-1 text-center font-medium w-[50px]">Post</th>
                      <th className="py-1 px-1 text-center font-medium w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/20">
                    {pageGroups.map(g => (
                      <tr key={g.id} className={`hover:bg-zinc-800/30 transition-colors ${!g.enabled ? 'opacity-50' : ''}`}>
                        <td className="py-1 pl-4 pr-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1">
                              <EditableNameCell value={g.group_name} onChange={v => handleUpdate(g.id, 'group_name', v)} />
                              <a href={g.group_url} target="_blank" rel="noopener" className="text-zinc-600 hover:text-indigo-400 shrink-0" title={g.group_url}>
                                <ExternalLink className="h-2.5 w-2.5" />
                              </a>
                            </div>
                            <div className="truncate text-[10px] text-zinc-600">{g.group_url.replace(/^https?:\/\/(www\.)?facebook\.com/, '')}</div>
                          </div>
                        </td>
                        <td className="py-1 px-1 text-center">
                          <ToggleCell value={g.enabled} onChange={v => handleUpdate(g.id, 'enabled', v)} />
                        </td>
                        <td className="py-1 px-1">
                          <SelectCell
                            value={g.post_type}
                            options={TYPE_OPTIONS}
                            onChange={v => handleUpdate(g.id, 'post_type', v)}
                          />
                        </td>
                        <td className="py-1 px-1">
                          <MultiFolderDropdown
                            selected={g.default_media_folder_ids ?? []}
                            folders={folders}
                            onChange={ids => handleUpdate(g.id, 'default_media_folder_ids', ids)}
                          />
                        </td>
                        <td className="py-1 px-1">
                          <SelectCell
                            value={String(g.post_interval_hours)}
                            options={INTERVAL_OPTIONS}
                            onChange={v => handleUpdate(g.id, 'post_interval_hours', parseInt(v))}
                          />
                        </td>
                        <td className="py-1 px-1 text-center">
                          <TextPopoverCell
                            postText={g.post_text}
                            commentText={g.comment_text}
                            onSave={(post, comment) => updateGroup.mutate({ id: g.id, post_text: post, comment_text: comment })}
                          />
                        </td>
                        <td className="py-1 px-1 text-center text-[10px] text-zinc-500" title={g.last_post_at || 'Nunca'}>
                          {timeAgo(g.last_post_at)}
                        </td>
                        <td className="py-1 px-1 text-center">
                          <button
                            onClick={() => {
                              if (confirm(`Remover "${g.group_name}"?`)) deleteGroup.mutate(g.id)
                            }}
                            className="rounded p-0.5 text-zinc-600 hover:bg-red-500/10 hover:text-red-400"
                            title="Remover grupo"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {!collapsed.has(`page:${conn.id}`) && pageGroups.length === 0 && (
                <div className="px-4 py-3 text-center text-[11px] text-zinc-600">
                  Nenhum grupo · <button onClick={() => setAddingTo(conn.id)} className="text-indigo-400 hover:underline">adicionar</button>
                </div>
              )}
            </div>
          )})}
        </div>
      )}

      {/* Clone modal */}
      {cloningFrom && (
        <CloneModal
          sourceConnectionId={cloningFrom}
          connections={connections}
          groups={groups}
          onClose={() => setCloningFrom(null)}
        />
      )}
    </div>
  )
}
